import { useState, useEffect } from "react";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import {
  Shield, Inbox, Building2, Users, Mail, Plus, Copy, Check,
  ArrowRight, Loader2, AlertTriangle, X, Settings, RefreshCw,
  Trash2, Edit,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const SPECIALTIES = [
  "rheumatology","dermatology","oncology","endocrinology",
  "gastroenterology","neurology","cardiology","respiratory",
  "nephrology","urology","haematology","ophthalmology",
  "orthopaedics","paediatrics","psychiatry","radiology",
];

interface Hospital {
  id: number; name: string; nhsTrustName: string; region?: string; city?: string; isActive: boolean;
}

interface Inbox {
  id: number; hospitalId: number; specialty: string; displayName: string;
  email: string | null; phone: string | null; hours: string | null; sla: string | null; isActive: boolean;
}

// ── Inboxes Section ──────────────────────────────────────────────────

function InboxSection({ hospital }: { hospital: Hospital }) {
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [form, setForm] = useState({ specialty: "rheumatology", displayName: "", email: "", phone: "", hours: "", sla: "" });
  const [editing, setEditing] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => fetch(`${BASE}/api/directory/hospitals/${hospital.id}/inboxes`).then(r => r.ok ? r.json() : []).then(setInboxes);

  useEffect(() => { load(); }, [hospital.id]);

  const handleSave = async () => {
    if (!form.displayName.trim()) return;
    setSaving(true); setError(null);
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing
        ? `${BASE}/api/admin/content/hospitals/${hospital.id}/inboxes/${editing}`
        : `${BASE}/api/admin/content/hospitals/${hospital.id}/inboxes`;
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({ error: "Failed" })); throw new Error(e.error); }
      await load();
      setForm({ specialty: "rheumatology", displayName: "", email: "", phone: "", hours: "", sla: "" });
      setEditing(null);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this inbox?")) return;
    try {
      await fetch(`${BASE}/api/admin/content/hospitals/${hospital.id}/inboxes/${id}`, { method: "DELETE", credentials: "include" });
      await load();
    } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">Advice Line Inboxes</p>
        <p className="text-xs text-slate-400">{inboxes.length} inbox(es)</p>
      </div>

      {inboxes.length === 0 && <p className="text-xs text-slate-400 italic">No inboxes configured yet.</p>}

      {inboxes.map(inb => (
        <div key={inb.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 capitalize">{inb.displayName}</p>
            <p className="text-xs text-slate-500">{inb.email ?? "No email"} · {inb.phone ?? "No phone"} {inb.hours ? `· ${inb.hours}` : ""}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => { setEditing(inb.id); setForm({ specialty: inb.specialty, displayName: inb.displayName, email: inb.email ?? "", phone: inb.phone ?? "", hours: inb.hours ?? "", sla: inb.sla ?? "" }); }}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors">
              <Edit className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => handleDelete(inb.id)}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}

      <div className="border border-slate-200 rounded-xl p-3 space-y-3 bg-white mt-3">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{editing ? "Edit inbox" : "Add inbox"}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px]">Specialty</Label>
            <select value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg">
              {SPECIALTIES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Display Name</Label>
            <Input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              placeholder="e.g. Rheumatology Advice Line" className="text-xs py-1.5" />
          </div>
          <div>
            <Label className="text-[10px]">Email</Label>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="rheumatology@nhs.net" className="text-xs py-1.5" />
          </div>
          <div>
            <Label className="text-[10px]">Phone</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="01283 741926" className="text-xs py-1.5" />
          </div>
          <div>
            <Label className="text-[10px]">Hours</Label>
            <Input value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
              placeholder="Mon-Fri 9-5" className="text-xs py-1.5" />
          </div>
          <div>
            <Label className="text-[10px]">SLA</Label>
            <Input value={form.sla} onChange={e => setForm(f => ({ ...f, sla: e.target.value }))}
              placeholder="e.g. 4 working hours" className="text-xs py-1.5" />
          </div>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 rounded-full text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            {editing ? "Update" : "Add"} inbox
          </Button>
          {editing && <Button size="sm" variant="outline" onClick={() => { setEditing(null); setForm({ specialty: "rheumatology", displayName: "", email: "", phone: "", hours: "", sla: "" }); }}
            className="rounded-full text-xs">Cancel</Button>}
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Panel ─────────────────────────────────────────────────

export default function AdvicelineAdmin() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<number | null>(null);
  const [addingHospital, setAddingHospital] = useState(false);
  const [newHospital, setNewHospital] = useState({ name: "", nhsTrustName: "", region: "Midlands", city: "" });
  const [savingHosp, setSavingHosp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imapConfig, setImapConfig] = useState({ host: "", user: "", password: "" });
  const [imapSaved, setImapSaved] = useState(false);

  const loadHospitals = () => {
    fetch(`${BASE}/api/directory/hospitals`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setHospitals)
      .catch(() => setError("Failed to load hospitals"));
  };

  useEffect(() => { loadHospitals(); }, []);

  // Load current IMAP config
  useEffect(() => {
    fetch(`${BASE}/api/admin/ingest-emails/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then((data: any) => {
        if (data) {
          setImapConfig(prev => ({
            host: data.host?.includes("✓") ? "Configured" : data.host ?? "",
            user: data.user?.includes("✓") ? "Configured" : data.user ?? "",
            password: data.password ? "Configured" : "",
          }));
        }
      })
      .catch(() => {});
  }, []);

  const handleAddHospital = async () => {
    if (!newHospital.name.trim() || !newHospital.nhsTrustName.trim() || !newHospital.city.trim()) {
      setError('Please fill in all required fields (name, trust, city)');
      return;
    }
    setSavingHosp(true); setError(null);
    try {
      const res = await fetch(`${BASE}/api/admin/content/hospitals`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newHospital),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? "Failed"); }
      await loadHospitals();
      setNewHospital({ name: "", nhsTrustName: "", region: "Midlands", city: "" });
      setAddingHospital(false);
    } catch (e: any) { setError(e.message); }
    finally { setSavingHosp(false); }
  };

  const handleToggleContracted = async (h: Hospital) => {
    const wasContracted = (hospitals as any).find((x: any) => x.id === h.id)?.contracted;
    try {
      // Toggle via hospitals API — Patch endpoint
      const res = await fetch(`${BASE}/api/admin/content/hospitals/${h.id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...h, contracted: true }),
      });
      if (!res.ok) throw new Error("Failed");
      await loadHospitals();
    } catch {}
  };

  return (
    <AdvicelineNurseLayout title="Admin Panel">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-amber-100 p-2.5 rounded-xl">
            <Shield className="h-6 w-6 text-amber-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-sm text-slate-500">Manage hospitals, inboxes, teams, and IMAP settings</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {/* ── IMAP SETTINGS ───────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            <h2 className="font-bold text-slate-900">Email Auto-Ingestion (IMAP)</h2>
          </div>
          <p className="text-xs text-slate-500">Connect to your shared NHS inbox to auto-import patient emails as draft requests.</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">IMAP Host</Label>
              <Input value={imapConfig.host} onChange={e => setImapConfig(f => ({ ...f, host: e.target.value }))}
                placeholder="imap.nhs.net" className="text-xs" />
            </div>
            <div>
              <Label className="text-xs">Username</Label>
              <Input value={imapConfig.user} onChange={e => setImapConfig(f => ({ ...f, user: e.target.value }))}
                placeholder="adviceline@nhs.net" className="text-xs" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input type="password" value={imapConfig.password} onChange={e => setImapConfig(f => ({ ...f, password: e.target.value }))}
                placeholder="••••••••" className="text-xs" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-xs" disabled>Save (manual setup)</Button>
            <Button size="sm" variant="outline" className="rounded-full text-xs" disabled>Test connection</Button>
          </div>
          <p className="text-xs text-amber-600">IMAP credentials must be added server-side in the .env file. This UI will trigger ingestion once configured.</p>
        </div>

        {/* ── HOSPITALS ──────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-600" />
              <p className="text-sm font-semibold text-slate-700">Contracted Hospitals ({hospitals.length})</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setAddingHospital(!addingHospital)}
              className="rounded-full text-xs">{addingHospital ? "Cancel" : "+ Add hospital"}</Button>
          </div>

          {addingHospital && (
            <div className="px-5 py-4 border-b border-slate-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input value={newHospital.name} onChange={e => setNewHospital(f => ({ ...f, name: e.target.value }))}
                  placeholder="Hospital name" className="text-xs" />
                <Input value={newHospital.nhsTrustName} onChange={e => setNewHospital(f => ({ ...f, nhsTrustName: e.target.value }))}
                  placeholder="NHS Trust name" className="text-xs" />
                <Input value={newHospital.city} onChange={e => setNewHospital(f => ({ ...f, city: e.target.value }))}
                  placeholder="City *" className="text-xs" />
                <select value={newHospital.region} onChange={e => setNewHospital(f => ({ ...f, region: e.target.value }))}
                  className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg">
                  <option>Midlands</option><option>London</option><option>North West</option><option>South East</option>
                  <option>South West</option><option>East of England</option><option>North East and Yorkshire</option>
                  <option>East Midlands</option><option>West Midlands</option><option>Wales</option><option>Scotland</option>
                </select>
              </div>
              <Button size="sm" onClick={handleAddHospital} disabled={savingHosp}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-xs">
                {savingHosp ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add hospital
              </Button>
            </div>
          )}

          {hospitals.map(h => (
            <div key={h.id} className="border-b border-slate-50 last:border-0">
              <button
                onClick={() => setSelectedHospital(selectedHospital === h.id ? null : h.id)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{h.name}</p>
                  <p className="text-xs text-slate-500">{h.nhsTrustName}{h.city ? ` · ${h.city}` : ""}</p>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">Contracted</span>
              </button>
              {selectedHospital === h.id && (
                <div className="px-5 pb-4">
                  <InboxSection hospital={h} />
                </div>
              )}
            </div>
          ))}
          {hospitals.length === 0 && <p className="px-5 py-4 text-xs text-slate-400 text-center">No hospitals. Add one above.</p>}
        </div>

        {/* ── QUICK LINKS ────────────────────────────────────────────── */}
        <div className="flex gap-3 flex-wrap">
          <Link href="/nurse/team" className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-xl text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors">
            <Users className="h-4 w-4" /> Team management <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </AdvicelineNurseLayout>
  );
}
